import { inject } from '@angular/core';
import {
  AggregatedStatusResponse,
  DeviceChannelConfiguration,
  EventBasedStatus,
  LatestStatusCollection,
  LatestStatusResponse,
} from '@core/models';
import { DeviceService } from '@core/services';
import { StatusService } from '@core/services/status.service';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Observable, pipe, switchMap, tap, of } from 'rxjs';

type StatusState = {
  isLoading: boolean;
  latest: LatestStatusResponse[];
  all: AggregatedStatusResponse[];
  count: number;
  channelConfigurations: { deviceId: number; configuration: DeviceChannelConfiguration }[];
  requestedChannelConfigurationIds: number[];
};

const initialState: StatusState = {
  all: [],
  isLoading: false,
  latest: [],
  count: 0,
  channelConfigurations: [],
  requestedChannelConfigurationIds: [],
};

export const StatusStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods(
    (store, statusService = inject(StatusService), deviceService = inject(DeviceService)) => ({
      getChannelConfigurationsMethod: rxMethod<number[]>(
        pipe(
          switchMap(deviceIds => {
            const missingIds = deviceIds.filter(
              (id: number) =>
                !store.requestedChannelConfigurationIds().some((r: number) => r === id),
            );
            if (missingIds.length === 0) {
              return of([]);
            }
            return deviceService.getChannelConfigurations(missingIds).pipe(
              tapResponse({
                next: (data: { deviceId: number; configuration: DeviceChannelConfiguration }[]) => {
                  patchState(store, state => ({
                    channelConfigurations: [...state.channelConfigurations, ...data],
                    requestedChannelConfigurationIds: [
                      ...state.requestedChannelConfigurationIds,
                      ...missingIds,
                    ],
                  }));
                },
                error: () => {
                  patchState(store, state => ({
                    isLoading: false,
                    requestedChannelConfigurationIds: [
                      ...state.requestedChannelConfigurationIds,
                      ...missingIds,
                    ],
                  }));
                },
              }),
            );
          }),
        ),
      ),
      getLatests: rxMethod<{ index: number; page_size: number }>(
        pipe(
          tap(() =>
            patchState(store, {
              isLoading: true,
              channelConfigurations: [],
              requestedChannelConfigurationIds: [],
            }),
          ),
          switchMap(({ index, page_size }) => {
            return statusService.getLatestPartnerOverviewStatus(index, page_size).pipe(
              tapResponse({
                next: (data: LatestStatusCollection) => {
                  patchState(store, {
                    isLoading: false,
                    latest: data.items,
                    count: data.item_count,
                  });
                },
                error: () => {
                  patchState(store, { isLoading: false });
                },
              }),
            );
          }),
        ),
      ),
      getAll: rxMethod<{ index: number; page_size: number }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(({ index, page_size }) => {
            return statusService.getAllPartnerOverviewStatus(index, page_size).pipe(
              tapResponse({
                next: (data: AggregatedStatusResponse[]) => {
                  patchState(store, {
                    isLoading: false,
                    all: data,
                  });
                },
                error: () => {
                  patchState(store, { isLoading: false });
                },
              }),
            );
          }),
        ),
      ),
      getSingle: (device_id: string) => {
        return store
          .latest()
          .filter((x: LatestStatusResponse) => x.type == 'event')
          .find((x: LatestStatusResponse) => (x.item as EventBasedStatus).device_id == device_id)
          ?.item as EventBasedStatus;
      },
      getChannelConfiguration: (device_id: string | number) => {
        return store
          .channelConfigurations()
          .find((x: { deviceId: number }) => x.deviceId == device_id);
      },
    }),
  ),
);
